import { CheckIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { ReactNode, useState } from "react";

interface EditableInputProps {
  initialValue?: string;
  placeholder?: string;
  renderInput: (props: {
    value: string;
    onChange: (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => void;
    onKeyDown: (
      e: React.KeyboardEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => void;
    onBlur: () => void;
    autoFocus: boolean;
  }) => ReactNode;
  onConfirm?: (value: string) => void;
  className?: string;
  useEnterToConfirm?: boolean;
}

const EditableInput = ({
  renderInput,
  initialValue,
  onConfirm,
  className,
  placeholder,
  useEnterToConfirm = true,
}: EditableInputProps) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [value, setValue] = useState<string>(initialValue ?? "");
  const [tempValue, setTempValue] = useState<string>(initialValue ?? "");

  const handleValueClick = () => {
    setIsEditing(true);
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setTempValue(e.target.value);
  };

  const handleConfirm = () => {
    setValue(tempValue);
    setIsEditing(false);

    if (onConfirm) {
      onConfirm(tempValue);
    }
  };

  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
  };

  const handleButtonClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    e.preventDefault();
    action();
  };

  const onBlur = () => {
    setIsEditing(false);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    if (e.key === "Enter" && useEnterToConfirm) {
      handleConfirm();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  return (
    <div className={className}>
      {isEditing ? (
        <div>
          {renderInput({
            value: tempValue,
            onChange: handleChange,
            onKeyDown: handleKeyDown,
            onBlur: onBlur,
            autoFocus: true,
          })}
          <div className="ml-auto mt-1 flex justify-end space-x-1">
            <button
              className="p-2 shadow-md bg-white rounded-md"
              aria-label="Bestätigen"
              onMouseDown={(e) => handleButtonClick(e, handleConfirm)}
            >
              <CheckIcon className="size-4" />
            </button>
            <button
              className="p-2 shadow-md bg-white rounded-md"
              aria-label="Verwerfen"
              onMouseDown={(e) => handleButtonClick(e, handleCancel)}
            >
              <XMarkIcon className="size-4" />
            </button>
          </div>
        </div>
      ) : (
        <div
          className="w-full rounded-md px-2 py-1 transition-colors duration-300 hover:bg-th-accent-200"
          onClick={handleValueClick}
        >
          {tempValue ? tempValue : <span className="italic text-gray-500">{placeholder}</span>}
        </div>
      )}
    </div>
  );
};

export default EditableInput;
